import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Handshake } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { Eyebrow, Button } from "../components/Bits";
import { submitContact } from "../lib/api";

const details = [
  { icon: Mail, label: "General enquiries", value: "hello@nead.uk", href: "mailto:hello@nead.uk" },
  { icon: Handshake, label: "Sponsorship & partnerships", value: "rocco.boyd@nead.uk", href: "mailto:rocco.boyd@nead.uk" },
  { icon: Phone, label: "Phone", value: "01946 389606", href: "tel:+441946389606" },
  { icon: MapPin, label: "Head office", value: "Top floor, Colours Diversity & Inclusion Hub, Whitehaven, Cumbria", href: null },
];
const field = "w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-base text-nead-navy placeholder:text-nead-navy/40 outline-none focus:border-nead-pink transition-colors";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitContact(form);
      toast.success("Thanks for getting in touch. We'll reply soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader label="Contact" titleLines={["Get in touch."]}
        intro="Whether you're a partner, a volunteer, or just curious about our work, we'd love to hear from you." />

      <section className="container-x py-24 md:py-32">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <Reveal>
            <Eyebrow className="text-nead-pink">Details</Eyebrow>
            <div className="mt-8 space-y-4">
              {details.map((d) => (
                <div key={d.label} className="flex items-start gap-5 rounded-2xl border border-black/[0.07] p-5" data-testid={`contact-info-${d.label.toLowerCase()}`}>
                  <div className="flex h-11 w-11 flex-none items-center justify-center rounded-full grad-pink-cyan">
                    <d.icon className="h-5 w-5 text-white" strokeWidth={1.8} />
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-nead-navy/45">{d.label}</div>
                    {d.href ? (
                      <a href={d.href} className="mt-1 block font-display font-bold text-lg text-nead-navy hover:text-nead-pink transition-colors">{d.value}</a>
                    ) : (
                      <div className="mt-1 font-display font-bold text-lg text-nead-navy">{d.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 overflow-hidden rounded-[1.5rem] border border-black/[0.07] aspect-[16/9]">
              <iframe title="NEAD head office, Colours Diversity and Inclusion Hub, Whitehaven" src="https://www.google.com/maps?q=Northern+Equality+and+Diversity+Whitehaven&output=embed" className="h-full w-full" loading="lazy" />
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-[1.75rem] bg-nead-mist border border-black/[0.06] p-8">
              <Eyebrow className="text-nead-pink">Send a message</Eyebrow>
              <form onSubmit={submit} className="mt-6 space-y-5" data-testid="contact-form">
                <input required value={form.name} onChange={set("name")} placeholder="Your name" aria-label="Name" className={field} data-testid="contact-name" />
                <input required type="email" value={form.email} onChange={set("email")} placeholder="Email address" aria-label="Email" className={field} data-testid="contact-email" />
                <input value={form.subject} onChange={set("subject")} placeholder="Subject" aria-label="Subject" className={field} data-testid="contact-subject" />
                <textarea required value={form.message} onChange={set("message")} placeholder="Your message" aria-label="Message" rows={5} className={`${field} resize-none`} data-testid="contact-message" />
                <Button type="submit" disabled={busy} testId="contact-submit">{busy ? "Sending…" : "Send message"}</Button>
              </form>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
